import jsPDF from 'jspdf';

export interface TextBlock {
  title?: string;
  paragraphs: string[];
}

export interface PaginatedTextSectionLayout {
  pageWidth: number;
  pageHeight: number;
  margin: number;
  textLeftMargin?: number;
  textRightMargin?: number;
  textPadding?: number;
  cornerRadius?: number;
  lineHeight?: number;
  sectionTitleY?: number;
}

export interface PaginatedTextSectionOptions {
  sectionTitle: string;
  blocks: TextBlock[];
  layout: PaginatedTextSectionLayout;
  setupPage: () => void | Promise<void>;
  startNewPage?: boolean;
  bgColor?: string;
}

interface SplitLine {
  text: string;
  isLast: boolean;
}

export const justifyText = (
  text: string,
  maxWidth: number,
  pdf: jsPDF,
  isLastLine: boolean = false
): string => {
  const words = text.trim().split(/\s+/);
  if (words.length <= 1 || isLastLine) return text.trim();

  const fontSize = pdf.getFontSize();
  const scaleFactor = pdf.internal.scaleFactor;
  const currentWidth =
    (pdf.getStringUnitWidth(words.join(' ')) * fontSize) / scaleFactor;
  const spaceWidth =
    (pdf.getStringUnitWidth(' ') * fontSize) / scaleFactor;

  const gaps = words.length - 1;
  const extraSpace = maxWidth - currentWidth;
  const excessiveSpaceThreshold = maxWidth * 0.3;

  if (extraSpace > excessiveSpaceThreshold) {
    return text.trim();
  }

  if (
    extraSpace <= 0 ||
    !isFinite(extraSpace) ||
    !isFinite(spaceWidth) ||
    spaceWidth === 0
  ) {
    return text.trim();
  }

  const totalExtraSpaces = extraSpace / spaceWidth;
  const baseExtraPerGap = Math.floor(totalExtraSpaces / gaps);
  const remainder = Math.max(
    0,
    Math.round(totalExtraSpaces - baseExtraPerGap * gaps)
  );

  const baseSpace = ' '.repeat(1 + baseExtraPerGap);
  let result = '';

  words.forEach((word, index) => {
    result += word;
    if (index < gaps) {
      const extra = index < remainder ? ' ' : '';
      result += baseSpace + extra;
    }
  });

  return result;
};

const splitParagraphsToLines = (
  pdf: jsPDF,
  paragraphs: string[],
  textAreaWidth: number
): SplitLine[] => {
  const splitText: SplitLine[] = [];

  paragraphs.forEach((paragraph) => {
    const trimmedParagraph = paragraph.trim().replace(/\s+/g, ' ');
    if (!trimmedParagraph) {
      return;
    }

    const lines = pdf.splitTextToSize(trimmedParagraph, textAreaWidth);
    lines.forEach((line: string, idx: number) => {
      const cleanLine = line.trim();
      if (cleanLine) {
        splitText.push({
          text: cleanLine,
          isLast: idx === lines.length - 1,
        });
      }
    });
  });

  return splitText;
};

const calculateTextHeight = (
  lineCount: number,
  textPadding: number,
  lineHeight: number
): number => {
  return lineCount * lineHeight + textPadding * 2;
};

export interface MethodologicalNoteTableOptions {
  sectionTitle: string;
  blocks: TextBlock[];
  layout: PaginatedTextSectionLayout;
  setupPage: () => void | Promise<void>;
}

const CELL_PADDING_X = 2.2;
const CELL_PADDING_TOP = 3;
const CELL_PADDING_BOTTOM = 0.5;
const TITLE_BODY_GAP = 1.1;
const MIN_INTER_BLOCK_GAP = 0.8;

const getTitleLineHeight = (titleFontSize: number) => titleFontSize * 0.38;

const measureBlockContentHeight = (
  pdf: jsPDF,
  block: TextBlock,
  colWidth: number,
  titleFontSize: number,
  bodyFontSize: number,
  lineHeight: number
): number => {
  const textWidth = colWidth - CELL_PADDING_X * 2;
  let height = CELL_PADDING_TOP;

  if (block.title) {
    pdf.setFontSize(titleFontSize);
    const titleLines = pdf.splitTextToSize(block.title, textWidth);
    height += titleLines.length * getTitleLineHeight(titleFontSize) + TITLE_BODY_GAP;
  }

  const bodyText = block.paragraphs.join(' ');
  if (bodyText.trim()) {
    pdf.setFontSize(bodyFontSize);
    const lines = pdf.splitTextToSize(bodyText.trim(), textWidth);
    height += lines.length * lineHeight;
  }

  return height + CELL_PADDING_BOTTOM;
};

const balanceBlocksIntoColumns = (
  pdf: jsPDF,
  blocks: TextBlock[],
  colWidth: number,
  titleFontSize: number,
  bodyFontSize: number,
  lineHeight: number
): { left: TextBlock[]; right: TextBlock[] } => {
  const left: TextBlock[] = [];
  const right: TextBlock[] = [];
  let leftHeight = 0;
  let rightHeight = 0;

  blocks.forEach((block) => {
    const blockHeight = measureBlockContentHeight(
      pdf,
      block,
      colWidth,
      titleFontSize,
      bodyFontSize,
      lineHeight
    );

    if (leftHeight <= rightHeight) {
      left.push(block);
      leftHeight += blockHeight;
    } else {
      right.push(block);
      rightHeight += blockHeight;
    }
  });

  return { left, right };
};

const measureColumnHeight = (
  pdf: jsPDF,
  columnBlocks: TextBlock[],
  colWidth: number,
  titleFontSize: number,
  bodyFontSize: number,
  lineHeight: number,
  interBlockGap = MIN_INTER_BLOCK_GAP
): number => {
  if (columnBlocks.length === 0) {
    return 0;
  }

  const contentHeight = columnBlocks.reduce(
    (total, block) =>
      total +
      measureBlockContentHeight(
        pdf,
        block,
        colWidth,
        titleFontSize,
        bodyFontSize,
        lineHeight
      ),
    0
  );

  return contentHeight + (columnBlocks.length - 1) * interBlockGap;
};

const getInterBlockGap = (
  columnBlocks: TextBlock[],
  pdf: jsPDF,
  colWidth: number,
  titleFontSize: number,
  bodyFontSize: number,
  lineHeight: number,
  availableHeight: number
): number => {
  if (columnBlocks.length <= 1) {
    return 0;
  }

  const contentHeight = columnBlocks.reduce(
    (total, block) =>
      total +
      measureBlockContentHeight(
        pdf,
        block,
        colWidth,
        titleFontSize,
        bodyFontSize,
        lineHeight
      ),
    0
  );
  const fixedGaps = (columnBlocks.length - 1) * MIN_INTER_BLOCK_GAP;
  const slack = availableHeight - contentHeight - fixedGaps;
  const extraGap = Math.max(0, slack) / (columnBlocks.length - 1);
  return MIN_INTER_BLOCK_GAP + extraGap;
};

const drawBlockContent = (
  pdf: jsPDF,
  block: TextBlock,
  x: number,
  y: number,
  colWidth: number,
  titleFontSize: number,
  bodyFontSize: number,
  lineHeight: number
): void => {
  const textX = x + CELL_PADDING_X;
  const textWidth = colWidth - CELL_PADDING_X * 2;
  const titleLineHeight = getTitleLineHeight(titleFontSize);
  let textY = y + CELL_PADDING_TOP;

  if (block.title) {
    pdf.setFontSize(titleFontSize);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(0, 0, 0);
    const titleLines = pdf.splitTextToSize(block.title, textWidth);
    titleLines.forEach((line: string) => {
      pdf.text(line, textX, textY);
      textY += titleLineHeight;
    });
    textY += TITLE_BODY_GAP;
  }

  const bodyText = block.paragraphs.join(' ');
  if (bodyText.trim()) {
    pdf.setFontSize(bodyFontSize);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(40, 40, 40);
    const bodyLines = pdf.splitTextToSize(bodyText.trim(), textWidth);
    bodyLines.forEach((line: string) => {
      pdf.text(line, textX, textY);
      textY += lineHeight;
    });
  }
};

const drawColumnBlocks = (
  pdf: jsPDF,
  columnBlocks: TextBlock[],
  x: number,
  startY: number,
  colWidth: number,
  titleFontSize: number,
  bodyFontSize: number,
  lineHeight: number,
  interBlockGap: number
): number => {
  let currentY = startY;

  columnBlocks.forEach((block, index) => {
    const blockHeight = measureBlockContentHeight(
      pdf,
      block,
      colWidth,
      titleFontSize,
      bodyFontSize,
      lineHeight
    );

    drawBlockContent(
      pdf,
      block,
      x,
      currentY,
      colWidth,
      titleFontSize,
      bodyFontSize,
      lineHeight
    );

    currentY += blockHeight;

    if (index < columnBlocks.length - 1) {
      pdf.setDrawColor(180, 180, 180);
      pdf.setLineWidth(0.15);
      pdf.line(x, currentY, x + colWidth, currentY);
      currentY += interBlockGap;
    }
  });

  return currentY - startY;
};

export const renderMethodologicalNoteTable = async (
  pdf: jsPDF,
  options: MethodologicalNoteTableOptions
): Promise<void> => {
  const { sectionTitle, blocks, layout, setupPage } = options;
  const {
    pageWidth,
    pageHeight,
    textLeftMargin = 32,
    textRightMargin = 10,
  } = layout;

  pdf.addPage();
  await setupPage();

  const sectionTitleY = 38;
  const tableTopY = 44;
  const TABLE_BOTTOM_MARGIN = 18;
  const tableBottomY = pageHeight - TABLE_BOTTOM_MARGIN;
  const availableHeight = tableBottomY - tableTopY;
  const tableWidth = pageWidth - textLeftMargin - textRightMargin;
  const colWidth = tableWidth / 2;

  const tableBlocks: TextBlock[] = blocks.map((block) => ({
    title: block.title,
    paragraphs: block.paragraphs,
  }));

  const measureTableAtFont = (
    bodyFontSize: number,
    titleFontSize: number,
    lineHeight: number,
    left: TextBlock[],
    right: TextBlock[]
  ) =>
    Math.max(
      measureColumnHeight(
        pdf,
        left,
        colWidth,
        titleFontSize,
        bodyFontSize,
        lineHeight
      ),
      measureColumnHeight(
        pdf,
        right,
        colWidth,
        titleFontSize,
        bodyFontSize,
        lineHeight
      )
    );

  let bodyFontSize = 6;
  let low = 5;
  let high = 10.5;
  let titleFontSize = 6.5;
  let lineHeight = 2.9;
  let leftBlocks: TextBlock[] = [];
  let rightBlocks: TextBlock[] = [];

  while (high - low > 0.15) {
    const mid = (low + high) / 2;
    const midTitle = mid + 0.6;
    const midLineHeight = mid * 0.48;
    const balanced = balanceBlocksIntoColumns(
      pdf,
      tableBlocks,
      colWidth,
      midTitle,
      mid,
      midLineHeight
    );
    const tableHeight = measureTableAtFont(
      mid,
      midTitle,
      midLineHeight,
      balanced.left,
      balanced.right
    );

    if (tableHeight <= availableHeight) {
      bodyFontSize = mid;
      titleFontSize = midTitle;
      lineHeight = midLineHeight;
      leftBlocks = balanced.left;
      rightBlocks = balanced.right;
      low = mid;
    } else {
      high = mid;
    }
  }

  if (leftBlocks.length === 0 || rightBlocks.length === 0) {
    const balanced = balanceBlocksIntoColumns(
      pdf,
      tableBlocks,
      colWidth,
      titleFontSize,
      bodyFontSize,
      lineHeight
    );
    leftBlocks = balanced.left;
    rightBlocks = balanced.right;
  }

  const leftInterBlockGap = getInterBlockGap(
    leftBlocks,
    pdf,
    colWidth,
    titleFontSize,
    bodyFontSize,
    lineHeight,
    availableHeight
  );
  const rightInterBlockGap = getInterBlockGap(
    rightBlocks,
    pdf,
    colWidth,
    titleFontSize,
    bodyFontSize,
    lineHeight,
    availableHeight
  );

  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(0, 0, 0);
  pdf.text(sectionTitle, textLeftMargin, sectionTitleY);

  const tableX = textLeftMargin;
  const leftColumnHeight = drawColumnBlocks(
    pdf,
    leftBlocks,
    tableX,
    tableTopY,
    colWidth,
    titleFontSize,
    bodyFontSize,
    lineHeight,
    leftInterBlockGap
  );
  const rightColumnHeight = drawColumnBlocks(
    pdf,
    rightBlocks,
    tableX + colWidth,
    tableTopY,
    colWidth,
    titleFontSize,
    bodyFontSize,
    lineHeight,
    rightInterBlockGap
  );
  const tableHeight = Math.max(leftColumnHeight, rightColumnHeight, 10);

  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(0.25);
  pdf.rect(tableX, tableTopY, tableWidth, tableHeight, 'S');
  pdf.line(
    tableX + colWidth,
    tableTopY,
    tableX + colWidth,
    tableTopY + tableHeight
  );
};

export const renderPaginatedTextSection = async (
  pdf: jsPDF,
  options: PaginatedTextSectionOptions
): Promise<void> => {
  const {
    sectionTitle,
    blocks,
    layout,
    setupPage,
    startNewPage = true,
    bgColor = '#FFFFFF',
  } = options;

  const {
    pageWidth,
    pageHeight,
    margin,
    textLeftMargin = 30,
    textRightMargin = 30,
    textPadding = 10,
    cornerRadius = 5,
    lineHeight = 5.5,
    sectionTitleY = 50,
  } = layout;

  const containerWidth = pageWidth - (textLeftMargin + textRightMargin);
  const textAreaWidth = containerWidth - textPadding * 2;
  const pageMaxY = pageHeight - margin;

  if (startNewPage) {
    pdf.addPage();
    await setupPage();
  }

  let currentY = sectionTitleY;
  let sectionTitleDrawn = false;

  const ensureSpace = async (requiredHeight: number) => {
    if (currentY + requiredHeight <= pageMaxY) {
      return;
    }

    pdf.addPage();
    await setupPage();
    currentY = margin + 15;
    sectionTitleDrawn = true;
  };

  const drawSectionTitleIfNeeded = async () => {
    if (sectionTitleDrawn) {
      return;
    }

    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.text(sectionTitle, textLeftMargin, currentY);
    currentY += 20;
    sectionTitleDrawn = true;
  };

  await drawSectionTitleIfNeeded();

  for (const block of blocks) {
    if (block.title) {
      const blockTitleHeight = 8;
      await ensureSpace(blockTitleHeight + 4);
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text(block.title, textLeftMargin, currentY);
      currentY += blockTitleHeight + 2;
    }

    const splitText = splitParagraphsToLines(pdf, block.paragraphs, textAreaWidth);
    if (splitText.length === 0) {
      continue;
    }

    let currentIndex = 0;
    while (currentIndex < splitText.length) {
      const availableHeight = pageMaxY - currentY - textPadding * 2;
      const maxLinesInPage = Math.max(1, Math.floor(availableHeight / lineHeight));
      const endLineIndex = Math.min(
        currentIndex + maxLinesInPage,
        splitText.length
      );
      const linesInCurrentPage = splitText.slice(currentIndex, endLineIndex);
      const textHeight = calculateTextHeight(
        linesInCurrentPage.length,
        textPadding,
        lineHeight
      );

      await ensureSpace(textHeight);

      pdf.setFillColor(bgColor);
      pdf.setDrawColor(0, 0, 0);
      pdf.roundedRect(
        textLeftMargin,
        currentY,
        containerWidth,
        textHeight,
        cornerRadius,
        cornerRadius,
        'S'
      );

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(0, 0, 0);

      let y = currentY + textPadding;
      linesInCurrentPage.forEach((lineObj) => {
        const justifiedLine = justifyText(
          lineObj.text.trim(),
          textAreaWidth,
          pdf,
          lineObj.isLast
        );
        pdf.text(justifiedLine, textLeftMargin + textPadding, y, {
          align: 'left',
        });
        y += lineHeight;
      });

      currentIndex = endLineIndex;
      currentY += textHeight + 6;

      if (currentIndex < splitText.length) {
        pdf.addPage();
        await setupPage();
        currentY = margin + 15;
      }
    }

    currentY += 4;
  }
};
